// Header, footer and the offline/engines panel.
import { effectiveTheme, toggleTheme } from '../app/prefs';
import { engineName } from '../app/engines';
import { OFFICE_ENABLED, SOURCE_URL, netserenoLink, resourcesLink } from '../config';
import { DICTS, fmt, formatBytes, t } from '../i18n';
import { OPTIONAL_ENGINES, type OptionalEngine, downloadSize, hasEngine, isEngineCached, prefetchEngine, removeEngine } from '../lib/engines/vendor';
import { LANGS, type Lang, type Route, enabledTools, homePath, pagePath, routePath, toolPath, translateRoute } from '../registry';
import { h } from './dom';
import { modal, progressView, toast } from './feedback';
import { icon } from './icons';

function netStatus(): HTMLElement {
  const d = t();
  const el = h('span', { class: 'net-status', role: 'status', hidden: navigator.onLine }, icon('wifi-off'), h('span', null, d.offline.offlineShort));
  el.title = d.offline.offline;
  const update = () => (el.hidden = navigator.onLine);
  window.addEventListener('online', update);
  window.addEventListener('offline', () => {
    update();
    toast(t().offline.offline, 'info');
  });
  return el;
}

export function header(route: Route, lang: Lang): HTMLElement {
  const d = t();
  const themeBtn = h('button', { class: 'icon-btn', type: 'button' });
  const paintTheme = () => {
    const dark = effectiveTheme() === 'dark';
    const label = dark ? d.nav.themeLight : d.nav.themeDark;
    themeBtn.replaceChildren(icon(dark ? 'sun' : 'moon'));
    themeBtn.setAttribute('aria-label', label);
    themeBtn.title = label;
  };
  themeBtn.addEventListener('click', () => {
    toggleTheme();
    paintTheme();
  });
  paintTheme();

  return h(
    'header',
    { class: 'site-header' },
    h('a', { class: 'skip-link', href: '#main' }, d.nav.skip),
    h(
      'div',
      { class: 'container' },
      h(
        'a',
        { class: 'brand', href: homePath(lang), 'aria-label': `${d.meta.siteName} · ${d.nav.home}` },
        h('img', { class: 'brand-logo', src: '/brand/netsereno-logo.png', alt: 'NetSereno', width: 175, height: 30 }),
        h('span', { class: 'brand-pdf' }, 'PDF'),
      ),
      h(
        'nav',
        { class: 'header-nav', 'aria-label': d.nav.menu },
        netStatus(),
        h('a', { class: 'hide-sm', href: `${homePath(lang)}#tools` }, d.nav.allTools),
        h('a', { class: 'hide-sm', href: pagePath(lang, 'privacy') }, d.nav.privacy),
        h('a', { class: 'hide-sm', href: resourcesLink('header'), target: '_blank', rel: 'noopener' }, d.nav.resources),
        h(
          'div',
          { class: 'lang-switch', role: 'group', 'aria-label': d.nav.language },
          LANGS.map((l) =>
            h(
              'a',
              { href: routePath(translateRoute(route, l)), hreflang: l, lang: l, 'aria-current': l === lang ? 'true' : undefined, title: DICTS[l].langName },
              l,
            ),
          ),
        ),
        themeBtn,
      ),
    ),
  );
}

export function footer(lang: Lang): HTMLElement {
  const d = t();
  const popular = enabledTools(OFFICE_ENABLED).filter((tool) => tool.popular).slice(0, 8);
  return h(
    'footer',
    { class: 'site-footer' },
    h(
      'div',
      { class: 'container' },
      h(
        'div',
        { class: 'footer-grid' },
        h(
          'div',
          null,
          h('div', { class: 'footer-brand' }, h('img', { class: 'brand-logo', src: '/brand/netsereno-logo.png', alt: 'NetSereno', width: 164, height: 28 }), h('span', { class: 'brand-pdf' }, 'PDF')),
          h('p', null, d.footer.resourceBy, ' ', h('a', { href: netserenoLink('footer'), target: '_blank', rel: 'noopener' }, 'NetSereno'), '. ', d.footer.about),
          h('p', null, d.footer.noCookies),
        ),
        h('div', null, h('h2', null, d.footer.toolsTitle), h('ul', null, popular.map((tool) => h('li', null, h('a', { href: toolPath(lang, tool.id) }, d.tools[tool.id].name))))),
        h(
          'div',
          null,
          h('h2', null, d.footer.infoTitle),
          h(
            'ul',
            null,
            h('li', null, h('a', { href: pagePath(lang, 'privacy') }, d.footer.privacy)),
            h('li', null, h('a', { href: pagePath(lang, 'licenses') }, d.footer.licenses)),
            h('li', null, h('a', { href: '#', onclick: (e: Event) => (e.preventDefault(), openOfflinePanel()) }, d.offline.panelTitle)),
            h('li', null, h('a', { href: resourcesLink('footer'), target: '_blank', rel: 'noopener' }, d.footer.resources, ' ', icon('external-link'))),
            h('li', null, h('a', { href: netserenoLink('footer'), target: '_blank', rel: 'noopener' }, d.footer.netsereno, ' ', icon('external-link'))),
            h('li', null, h('a', { href: SOURCE_URL, target: '_blank', rel: 'noopener' }, d.footer.source, ' ', icon('external-link'))),
          ),
        ),
      ),
      h('div', { class: 'footer-bottom' }, h('span', null, fmt(d.footer.rights, { year: new Date().getFullYear() })), h('span', null, `v${__APP_VERSION__}`)),
    ),
  );
}

const ENGINE_ICONS: Record<OptionalEngine, string> = { ocr: 'scan-text', gs: 'minimize-2', mupdf: 'eraser', office: 'file-text' };

/** Lists optional engines with their offline status and download/remove buttons. */
export function openOfflinePanel(): void {
  const d = t();
  const engines = OPTIONAL_ENGINES.filter(hasEngine);
  void modal<null>(
    (close) => {
      const list = h('ul', { class: 'engine-list' });
      const usage = h('p', { class: 'field-hint' });
      const refreshUsage = async () => {
        try {
          const est = await navigator.storage?.estimate?.();
          if (est?.usage !== undefined) usage.textContent = fmt(d.offline.storage, { used: formatBytes(est.usage) });
        } catch {
          // ignore
        }
      };
      const row = (id: OptionalEngine) => {
        const status = h('div', { class: 'file-meta' }, d.common.loading);
        const action = h('div');
        const item = h('li', { class: 'engine-item' }, icon(ENGINE_ICONS[id]), h('div', { class: 'file-info' }, h('div', { class: 'file-name' }, engineName(id)), status), action);
        const paint = async () => {
          const cached = await isEngineCached(id);
          status.textContent = `${cached ? d.engines.available : d.engines.notDownloaded} · ~${formatBytes(downloadSize(id))}`;
          action.replaceChildren(
            cached
              ? h('button', { class: 'btn btn-sm btn-outline', type: 'button', onclick: async () => (await removeEngine(id), paint(), refreshUsage()) }, icon('trash'), d.offline.remove)
              : h(
                  'button',
                  {
                    class: 'btn btn-sm btn-primary',
                    type: 'button',
                    onclick: async () => {
                      const p = progressView(fmt(d.engines.downloading, { engine: engineName(id), percent: '0 %' }));
                      action.replaceChildren(p.el);
                      try {
                        await prefetchEngine(id, (l, total) => p.set(l / total, `${Math.round((l / total) * 100)} %`));
                      } catch {
                        toast(d.errors.engineDownload, 'error');
                      }
                      await paint();
                      await refreshUsage();
                    },
                  },
                  icon('download'),
                  d.offline.prepare,
                ),
          );
        };
        void paint();
        return item;
      };
      list.append(...engines.map(row));
      void refreshUsage();
      return h(
        'div',
        { class: 'modal-body' },
        h('h2', null, d.offline.panelTitle),
        h('p', null, 'serviceWorker' in navigator ? d.offline.panelText : d.offline.unsupported),
        list,
        usage,
        h('div', { class: 'modal-actions' }, h('button', { class: 'btn btn-primary', type: 'button', onclick: () => close(null) }, d.common.close)),
      );
    },
    { label: d.offline.panelTitle },
  );
}
